<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import SingleDatePicker from 'dashboard/components/ui/DatePicker/SingleDatePicker.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  onClose: {
    type: Function,
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();
const accountId = computed(() => store.getters.getCurrentAccountId);
const currentAccount = computed(() =>
  store.getters['accounts/getAccount']?.(accountId.value)
);
const hasSummaryFeature = computed(
  () =>
    store.getters['accounts/isFeatureEnabledonAccount']?.(
      accountId.value,
      FEATURE_FLAGS.LUMERAI_SUMMARIES
    ) ||
    store.getters['accounts/isFeatureEnabledonAccount']?.(
      accountId.value,
      FEATURE_FLAGS.LUMERAI
    ) ||
    currentAccount.value?.is_freemium_ai === true ||
    currentAccount.value?.active_modules?.ia === true ||
    currentAccount.value?.active_modules?.ia === 'true'
);
const saving = ref(false);
const filling = ref(false);
const title = ref('');
const description = ref('');
const participantEmails = ref('');
const syncExternalCalendars = ref(false);
const hasExternalCalendarConnection = ref(false);
const availableSlots = ref([]);
const blockedDates = ref([]);
const maxDaysAhead = ref(30);
const loadingAvailability = ref(false);

const toLocalValue = date => {
  const pad = value => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
const start = new Date();
start.setSeconds(0, 0);
start.setMinutes(Math.ceil(start.getMinutes() / 30) * 30);
const end = new Date(start.getTime() + 30 * 60 * 1000);
const startsAt = ref(toLocalValue(start));
const endsAt = ref(toLocalValue(end));
const appointmentDate = ref(startsAt.value.slice(0, 10));
const matchingBlockedDate = dateValue => {
  const date = new Date(`${dateValue.slice(0, 10)}T12:00:00`);
  return blockedDates.value.find(blocked => {
    const base = new Date(`${blocked.date}T12:00:00`);
    if (Number.isNaN(base.getTime())) return false;
    if (blocked.recurrence === 'weekly') return base.getDay() === date.getDay();
    if (blocked.recurrence === 'monthly')
      return base.getDate() === date.getDate();
    if (blocked.recurrence === 'yearly') {
      return (
        base.getMonth() === date.getMonth() && base.getDate() === date.getDate()
      );
    }
    return base.toDateString() === date.toDateString();
  });
};

const loadAvailability = async dateValue => {
  if (!dateValue) return;
  loadingAvailability.value = true;
  try {
    const day = dateValue.slice(0, 10);
    const dayStart = new Date(`${day}T00:00:00`);
    const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
    const [availabilityResponse, settingsResponse] = await Promise.all([
      window.axios.get(
        `/api/v1/accounts/${accountId.value}/calendar/availability`,
        {
          params: {
            from: dayStart.toISOString(),
            to: dayEnd.toISOString(),
            agent_ids: store.getters.getCurrentUserID,
          },
        }
      ),
      window.axios.get(`/api/v1/accounts/${accountId.value}/calendar/settings`),
    ]);
    blockedDates.value = settingsResponse.data.payload?.blocked_dates || [];
    maxDaysAhead.value =
      settingsResponse.data.payload?.maximum_days_ahead ?? maxDaysAhead.value;
    const data = availabilityResponse.data;
    let slots = matchingBlockedDate(dateValue)
      ? []
      : (data.payload || []).flatMap(item => item.slots || []);

    // If the selected day is blocked or has no free slots, look ahead within
    // the account policy instead of opening the form on an unusable date.
    if (!slots.length) {
      const lastAllowedDate = new Date(dayStart);
      lastAllowedDate.setDate(
        lastAllowedDate.getDate() + maxDaysAhead.value + 1
      );
      const rangeResponse = await window.axios.get(
        `/api/v1/accounts/${accountId.value}/calendar/availability`,
        {
          params: {
            from: dayStart.toISOString(),
            to: lastAllowedDate.toISOString(),
            agent_ids: store.getters.getCurrentUserID,
          },
        }
      );
      slots = (rangeResponse.data.payload || [])
        .flatMap(item => item.slots || [])
        .filter(slot => !matchingBlockedDate(slot.starts_at));
    }

    availableSlots.value = slots;
    const currentSlot = availableSlots.value.find(
      slot =>
        toLocalValue(new Date(slot.starts_at)) === startsAt.value &&
        toLocalValue(new Date(slot.ends_at)) === endsAt.value
    );
    if (!currentSlot && availableSlots.value[0]) {
      startsAt.value = toLocalValue(
        new Date(availableSlots.value[0].starts_at)
      );
      endsAt.value = toLocalValue(new Date(availableSlots.value[0].ends_at));
      appointmentDate.value = startsAt.value.slice(0, 10);
    }
  } catch (error) {
    availableSlots.value = [];
  } finally {
    loadingAvailability.value = false;
  }
};
const loadExternalCalendarConnection = async () => {
  try {
    const { data } = await window.axios.get(
      `/api/v1/accounts/${accountId.value}/calendar/connections`
    );
    const currentUserId = String(store.getters.getCurrentUserID);
    hasExternalCalendarConnection.value = (data.payload || []).some(
      connection =>
        connection.active === true &&
        connection.status === 'active' &&
        ['google', 'microsoft'].includes(connection.provider) &&
        String(connection.user?.id) === currentUserId
    );
  } catch (error) {
    hasExternalCalendarConnection.value = false;
  }
};
onMounted(() => {
  loadAvailability(startsAt.value);
  loadExternalCalendarConnection();
});
watch(
  () => startsAt.value.slice(0, 10),
  dateValue => {
    appointmentDate.value = dateValue;
    loadAvailability(dateValue);
  }
);
watch(appointmentDate, dateValue => {
  if (!dateValue) return;
  const startTime = startsAt.value.slice(11) || '09:00';
  const endTime = endsAt.value.slice(11) || '09:30';
  startsAt.value = `${dateValue}T${startTime}`;
  endsAt.value = `${dateValue}T${endTime}`;
});

const fillWithAi = async () => {
  filling.value = true;
  try {
    const { data } = await window.axios.get(
      `/api/v1/accounts/${accountId.value}/conversations/${props.conversationId}/calendar_summary`
    );
    title.value = data.titulo || title.value;
    description.value = data.resumo || description.value;
    useAlert(t('CALENDAR.ALERTS.AI_FILLED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('CALENDAR.ALERTS.AI_FILL_ERROR'));
  } finally {
    filling.value = false;
  }
};

const save = async () => {
  if (!title.value.trim() || !startsAt.value || !endsAt.value) {
    useAlert(t('CALENDAR.ALERTS.REQUIRED_FIELDS'));
    return;
  }
  if (endsAt.value <= startsAt.value) {
    useAlert(t('CALENDAR.ALERTS.END_AFTER_START'));
    return;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const selectedDateValue = new Date(`${appointmentDate.value}T12:00:00`);
  const lastAllowedDate = new Date(today);
  lastAllowedDate.setDate(lastAllowedDate.getDate() + maxDaysAhead.value);
  if (selectedDateValue < today || selectedDateValue > lastAllowedDate) {
    useAlert(
      t('CALENDAR.ALERTS.SCHEDULE_AHEAD_LIMIT', { days: maxDaysAhead.value })
    );
    return;
  }
  const isAvailable = availableSlots.value.some(
    slot =>
      toLocalValue(new Date(slot.starts_at)) === startsAt.value &&
      toLocalValue(new Date(slot.ends_at)) === endsAt.value
  );
  if (!isAvailable) {
    const blockedDate = matchingBlockedDate(startsAt.value);
    useAlert(
      blockedDate
        ? t('CALENDAR.ALERTS.BLOCKED_DATE_MESSAGE', {
            description: blockedDate.description
              ? `: ${blockedDate.description}`
              : '',
          })
        : t('CALENDAR.ALERTS.AVAILABLE_SLOT_REQUIRED')
    );
    return;
  }

  saving.value = true;
  try {
    await window.axios.post(
      `/api/v1/accounts/${accountId.value}/calendar/appointments`,
      {
        calendar_appointment: {
          agent_id: store.getters.getCurrentUserID,
          conversation_id: props.conversationId,
          title: title.value.trim(),
          description: description.value.trim() || null,
          starts_at: startsAt.value,
          ends_at: endsAt.value,
          provider: 'internal',
          sync_external_calendars: syncExternalCalendars.value,
          participant_emails: participantEmails.value
            .split(',')
            .map(email => email.trim())
            .filter(Boolean),
        },
      }
    );
    props.onClose();
    useAlert(t('CALENDAR.ALERTS.AD_HOC_PERIOD_CREATED'));
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('CALENDAR.ALERTS.AD_HOC_PERIOD_ERROR')
    );
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <section class="w-full max-w-2xl p-6 text-n-slate-12">
    <h2 class="text-xl font-semibold">{{ t('CALENDAR.AD_HOC_FORM.TITLE') }}</h2>

    <div class="mt-5 grid gap-3 sm:grid-cols-2">
      <label class="text-sm text-n-slate-11">
        {{ t('CALENDAR.AD_HOC_FORM.START_TIME') }}
        <input
          type="time"
          :value="startsAt.slice(11)"
          class="mt-1 w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
          @input="startsAt = `${appointmentDate}T${$event.target.value}`"
        />
      </label>
      <label class="text-sm text-n-slate-11">
        {{ t('CALENDAR.AD_HOC_FORM.END_TIME') }}
        <input
          type="time"
          :value="endsAt.slice(11)"
          class="mt-1 w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
          @input="endsAt = `${appointmentDate}T${$event.target.value}`"
        />
      </label>
    </div>
    <label class="mt-4 block text-sm text-n-slate-11">
      {{ t('CALENDAR.AD_HOC_FORM.DATE') }}
      <SingleDatePicker
        v-model="appointmentDate"
        :blocked-dates="blockedDates"
        :max-days-ahead="maxDaysAhead"
      />
    </label>
    <p class="mt-2 text-xs text-n-slate-11">
      {{
        loadingAvailability
          ? t('CALENDAR.AD_HOC_FORM.LOADING_AVAILABILITY')
          : matchingBlockedDate(startsAt)
            ? t('CALENDAR.AD_HOC_FORM.DATE_UNAVAILABLE', {
                description: matchingBlockedDate(startsAt).description || '',
              })
            : availableSlots.length
              ? t('CALENDAR.AD_HOC_FORM.TIME_MUST_MATCH_AVAILABILITY')
              : t('CALENDAR.AD_HOC_FORM.NO_AVAILABLE_TIMES')
      }}
    </p>
    <p class="mt-2 text-xs text-n-slate-11">
      {{
        loadingAvailability
          ? t('CALENDAR.AD_HOC_FORM.LOADING_TIMES')
          : availableSlots.length
            ? t('CALENDAR.AD_HOC_FORM.PERIOD_MUST_MATCH_FREE_SLOT')
            : t('CALENDAR.AD_HOC_FORM.NO_AVAILABLE_TIMES')
      }}
    </p>

    <div class="mt-4 rounded-lg bg-n-solid-2 px-3 py-2 text-sm">
      {{ t('CALENDAR.AD_HOC_FORM.CONVERSATION', { id: conversationId }) }}
    </div>

    <label
      v-if="hasExternalCalendarConnection"
      class="mt-4 block text-sm text-n-slate-11"
    >
      {{ t('CALENDAR.AD_HOC_FORM.PARTICIPANTS') }}
      <input
        v-model="participantEmails"
        type="text"
        :placeholder="t('CALENDAR.AD_HOC_FORM.PARTICIPANTS_PLACEHOLDER')"
        class="mt-1 w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
      />
    </label>
    <label
      v-if="hasExternalCalendarConnection"
      class="mt-4 flex items-center gap-2 text-sm text-n-slate-11"
    >
      <input v-model="syncExternalCalendars" type="checkbox" />
      {{ t('CALENDAR.AD_HOC_FORM.SYNC_EXTERNAL_CALENDARS') }}
    </label>

    <button
      v-if="hasSummaryFeature"
      type="button"
      class="mt-3 inline-flex items-center rounded-lg border border-n-brand px-3 py-2 text-sm font-medium text-n-brand hover:bg-n-brand/10 disabled:opacity-60"
      :disabled="filling"
      @click="fillWithAi"
    >
      {{
        filling
          ? t('CALENDAR.AD_HOC_FORM.AI_FILLING')
          : t('CALENDAR.AD_HOC_FORM.AI_FILL')
      }}
    </button>

    <label class="mt-4 block text-sm text-n-slate-11">
      {{ t('CALENDAR.AD_HOC_FORM.APPOINTMENT_TITLE') }}
      <input
        v-model="title"
        type="text"
        :placeholder="t('CALENDAR.AD_HOC_FORM.TITLE_PLACEHOLDER')"
        class="mt-1 w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
      />
    </label>
    <label class="mt-4 block text-sm text-n-slate-11">
      {{ t('CALENDAR.AD_HOC_FORM.DESCRIPTION_OPTIONAL') }}
      <textarea
        v-model="description"
        rows="4"
        :placeholder="t('CALENDAR.AD_HOC_FORM.DESCRIPTION_PLACEHOLDER')"
        class="mt-1 w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
      />
    </label>

    <div class="mt-6 flex justify-end gap-3">
      <button
        type="button"
        class="rounded-lg px-4 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-2"
        @click="onClose"
      >
        {{ t('CALENDAR.AD_HOC_FORM.CANCEL') }}
      </button>
      <button
        type="button"
        class="rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
        :disabled="saving"
        @click="save"
      >
        {{
          saving
            ? t('CALENDAR.AD_HOC_FORM.SAVING')
            : t('CALENDAR.AD_HOC_FORM.SUBMIT')
        }}
      </button>
    </div>
  </section>
</template>
